import React from "react";
import { cn } from "@/lib/utils";

/* =============================================================================
   DAY-OF-MONTH PICKER  ·  AITS-494 (Reporting)
   -----------------------------------------------------------------------------
   Shared by the Reporting panel and ScheduleFormDialog so both write the same
   value for the same cell. Styling is `.vb-daygrid` / `.vb-day` (vb-settings.css).

   DAY ↔ BACKEND MAPPING
   `ReportScheduleSerializer.generation_day` is write-only (1–31) and the API
   only ever reads back `next_generation_ts`, so the selected day is back-derived
   from that timestamp. The grid offers 1–28 plus "Last":
     · 1–28  → generation_day = the same number (present in every month).
     · Last  → generation_day = 31; the serializer clamps it to the target
               month's real last day via calendar.monthrange, so it lands on the
               28th/29th in February and the 30th in April.
   29 and 30 are deliberately NOT offered: they are silently clamped in short
   months, which reads as a broken schedule rather than a choice. A stored 29/30
   therefore normalises onto "Last" when the grid loads it back.
   ============================================================================= */

/** What "Last" writes. The backend clamps it down per month. */
export const LAST_DAY_OF_MONTH = 31;

/** Snap an arbitrary stored/derived day onto a cell the grid actually has.
 *
 *  The API makes `generation_day` write-only and returns only
 *  `next_generation_ts`, so the day is always back-derived. Two consequences:
 *  a stored 29/30 shows as "Last", and a "Last" whose next run the backend
 *  clamped into a short month (Feb 28) reads back as day 28. Both are display
 *  artefacts of the write-only field, not lost settings. */
export function normalizeGenerationDay(day: number): number {
  if (!Number.isFinite(day) || day < 1) return 1;
  const whole = Math.trunc(day);
  return whole > 28 ? LAST_DAY_OF_MONTH : whole;
}

/** Mirrors `ReportScheduleSerializer.to_internal_value`: a day still ahead of
 *  today runs this month, otherwise next month, clamped to that month's length.
 *  Used only to preview the next run before the API has answered. */
export function computeNextRun(day: number, from: Date = new Date()): Date {
  const today = from.getDate();
  let month = from.getMonth();
  let year = from.getFullYear();
  if (day <= today) {
    month += 1;
    if (month > 11) {
      month = 0;
      year += 1;
    }
  }
  // Day 0 of the following month === the last day of this one.
  const lastDay = new Date(year, month + 1, 0).getDate();
  return new Date(year, month, Math.min(day, lastDay));
}

export function describeGenerationDay(day: number): string {
  return day === LAST_DAY_OF_MONTH
    ? "Last day of the month"
    : `Day ${day} of each month`;
}

export function formatRunDate(date: Date): string {
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

const DAYS = Array.from({ length: 28 }, (_, index) => index + 1);

type Props = {
  /** 1–28, or 31 for "Last". Anything else snaps via normalizeGenerationDay. */
  value: number;
  onChange: (day: number) => void;
  disabled?: boolean;
};

/** Button group (not a radiogroup) — every cell carries `aria-pressed`, so each
 *  one is individually tabbable and announces its own on/off state. The group
 *  label comes from the surrounding Field/FormRow in `group` mode. */
export function DayOfMonthGrid({ value, onChange, disabled }: Props) {
  const selected = normalizeGenerationDay(value);

  return (
    <div className="vb-daygrid">
      {DAYS.map((day) => (
        <button
          key={day}
          type="button"
          disabled={disabled}
          aria-pressed={selected === day}
          aria-label={`Day ${day} of each month`}
          className={cn("vb-day", selected === day && "vb-day--on")}
          onClick={() => onChange(day)}
        >
          {day}
        </button>
      ))}
      <button
        type="button"
        disabled={disabled}
        aria-pressed={selected === LAST_DAY_OF_MONTH}
        aria-label="Last day of the month"
        className={cn(
          "vb-day vb-day--last",
          selected === LAST_DAY_OF_MONTH && "vb-day--on",
        )}
        onClick={() => onChange(LAST_DAY_OF_MONTH)}
      >
        Last
      </button>
    </div>
  );
}

export default DayOfMonthGrid;
